import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../contexts/UserContext';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import i18n from '../i18n/i18n';

const ACCENT = '#be2e00';

// Custom dropdown that mirrors TopHeader's user-menu behaviour but matches the
// Galp design (node 323:219): the initials avatar toggles a right-aligned column
// of the exact Figma icon buttons (white bordered circle + dark glyph), the
// avatar becomes the Figma X on open. Built without MUI SpeedDial so the icon
// borders stay crisp (no fab circle/shadow rendered behind the SVGs).
const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${ACCENT};
  border: 1px solid #8f3214;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: Lato, sans-serif;
  font-size: 12px;
`;

const PlainButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  outline: none;
`;

type Props = { initials: string };

const GalpUserMenu = ({ initials }: Props) => {
  const [open, setOpen] = React.useState(false);
  const history = useHistory();
  const { signOut, loggedIn } = useUser();
  const { vendingMachineInfo, stickerId } = useVendingMachine();

  const suffix = stickerId ? `?s=${stickerId}` : '';

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (loggedIn || vendingMachineInfo?.loginType !== 'PRIVATE') {
      setOpen(true);
    } else {
      history.push('/');
    }
  };

  const run = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  const actions = [
    {
      name: i18n.t('languages'),
      iconSrc: '/companies/GALP/globe_icon.svg',
      onClick: () => history.push(`/languages${suffix}`),
    },
    {
      name: i18n.t('name'),
      iconSrc: '/companies/GALP/person_icon.svg',
      onClick: () => history.push(`/profile${suffix}`),
    },
    {
      name: i18n.t('password'),
      iconSrc: '/companies/GALP/key_icon.svg',
      onClick: () => history.push(`/password-change${suffix}`),
    },
    {
      name: i18n.t('search_documents'),
      iconSrc: '/companies/GALP/docs_icon.svg',
      onClick: () => history.push('/session/documents', { vendingMachineId: vendingMachineInfo?.id }),
    },
    {
      name: i18n.t('logout'),
      iconSrc: '/companies/GALP/logout_icon.svg',
      onClick: () => {
        signOut().then(() => {
          if (!vendingMachineInfo) {
            history.push('/');
          }
        });
      },
    },
  ];

  return (
    <>
      {open && (
        <div
          role="presentation"
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 19 }}
        />
      )}
      <div style={{
        position: 'absolute',
        top: '30px',
        right: '12px',
        width: '43px',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      >
        <PlainButton type="button" aria-label="user menu" onClick={toggle}>
          {open ? (
            <img
              src="/companies/GALP/x_icon.svg"
              alt=""
              style={{ width: '24px', height: '24px', display: 'block' }}
            />
          ) : (
            <Avatar>{initials}</Avatar>
          )}
        </PlainButton>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '11px',
          marginTop: '14px',
          pointerEvents: open ? 'auto' : 'none',
        }}
        >
          {actions.map((action, index) => (
            <PlainButton
              key={action.name}
              type="button"
              aria-label={action.name}
              onClick={run(action.onClick)}
              style={{
                opacity: open ? 1 : 0,
                transform: open ? 'translateY(0)' : 'translateY(-10px)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                transitionDelay: open ? `${index * 50}ms` : '0ms',
              }}
            >
              <img
                src={action.iconSrc}
                alt=""
                style={{ width: '43px', height: '45px', display: 'block' }}
              />
            </PlainButton>
          ))}
        </div>
      </div>
    </>
  );
};

export default GalpUserMenu;
